import React, { useState, useRef, useCallback } from 'react';
import { THEME } from '../../styles/theme';
import { getRules, getKnowledgeChunks, getKnowledge, saveKnowledge } from '../../services/storage';

const BASE_VISION_PROMPT = `You are JARVIS Chart Vision — an expert XAU/USD technical analyst with deep ICT/SMC knowledge.
Analyze this trading chart screenshot and provide a complete, structured analysis.

Identify and report EVERYTHING you can see:

1. TIMEFRAME & PAIR — What pair and timeframe is this chart?

2. TREND & BIAS — Overall direction (bullish/bearish/sideways). Recent structure.

3. ORDER BLOCKS (OBs) — Identify ALL visible OBs:
   - Bullish OBs (potential buy zones) with approximate price levels
   - Bearish OBs (potential sell zones) with approximate price levels
   - Are they fresh (unmitigated) or already tested?
   - For each OB: calculate the MIDPOINT (0.5 level) — this is the OB Fib 0.5 entry zone
   - Bullish OB: lower half (below midpoint) = BUY zone (Blue zone)
   - Bearish OB: upper half (above midpoint) = SELL zone (Red zone)

4. FAIR VALUE GAPS (FVGs) — Identify all visible imbalances/FVGs:
   - Bullish FVGs (buy-side gaps)
   - Bearish FVGs (sell-side gaps)
   - Price levels of each gap

5. MARKET STRUCTURE — Recent BOS (Break of Structure) or CHoCH (Change of Character)?
   - Direction of latest BOS/CHoCH
   - Current structural state
   - BOS = trend continuation. CHoCH = first reversal warning (wait for confirmation)

6. LIQUIDITY — Where is liquidity resting?
   - Equal highs (EQH) / equal lows (EQL)
   - Previous swing highs/lows being targeted
   - Any swept liquidity visible

7. FIBONACCI LEVELS (if visible) — Key retracement levels.
   Sir's personal levels: 0.15 (deep buy), 0.75 (OTE buy), 0.84 (sell), 0.90 (final sell)

8. KEY SUPPORT & RESISTANCE — Major horizontal levels visible on chart.

9. CURRENT PRICE CONTEXT — Where is price relative to all the above?

10. TRADE RECOMMENDATION — Based on what you see:
    - Bias: BUY / SELL / NEUTRAL / WAIT
    - Entry zone (approximate price) — prefer OB Fib 0.5 zone
    - Stop loss level (below/above full OB)
    - Target 1 and Target 2
    - Grade: A+ / A / B / C / D
    - Why this setup specifically

Be specific with price levels if visible. If price is not visible, describe relative position.
Address Sir directly. Reply in same language Sir used (Marathi or English).`;

// Build dynamic prompt — injects Sir's saved rules and knowledge chunks
function buildDynamicVisionPrompt(userQuestion) {
  const activeRules   = getRules().filter(r => r.status === 'active');
  const chunks        = getKnowledgeChunks();
  const q             = (userQuestion || '').toLowerCase();

  // Find relevant knowledge chunks (simple keyword match)
  const relevantChunks = chunks.length > 0
    ? chunks
        .map(c => {
          const text  = `${c.title} ${c.content} ${(c.tags || []).join(' ')}`.toLowerCase();
          const words = q.split(/\s+/).filter(w => w.length > 2);
          const score = words.filter(w => text.includes(w)).length;
          return { ...c, score };
        })
        .filter(c => c.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 4)
    : [];

  let prompt = BASE_VISION_PROMPT;

  if (activeRules.length > 0) {
    prompt += `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
SIR'S SAVED RULES — APPLY THESE TO THIS CHART
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${activeRules.slice(0, 10).map(r =>
  `[${r.id}] ${r.title}
  Condition : ${r.condition || 'N/A'}
  Action    : ${r.action} | ICT: ${r.ict_concept || 'N/A'} | Session: ${r.session || 'Any'}
  Source    : ${r.source || 'Manual'}`
).join('\n\n')}

IMPORTANT: Check each visible OB/FVG/level on this chart against Sir's rules above.
Tell Sir specifically: which rule does this chart setup satisfy? Which ones are missing?`;
  }

  if (relevantChunks.length > 0) {
    prompt += `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
RETRIEVED KNOWLEDGE (relevant to this analysis)
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
${relevantChunks.map(c => `[${c.category.toUpperCase()} — ${c.source}] ${c.title}: ${c.content}`).join('\n\n')}`;
  }

  return prompt;
}

const S = {
  wrap:      { padding: '20px', maxWidth: '1000px', margin: '0 auto' },
  title:     { fontSize: '22px', fontWeight: '800', color: THEME.colors.blue, fontFamily: THEME.fonts.hud, letterSpacing: '2px', marginBottom: '6px' },
  sub:       { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginBottom: '24px' },

  dropzone:  (drag) => ({
    border:      `2px dashed ${drag ? THEME.colors.blue : 'rgba(0,212,255,0.2)'}`,
    borderRadius:'14px',
    padding:     '40px 20px',
    textAlign:   'center',
    cursor:      'pointer',
    background:  drag ? 'rgba(0,212,255,0.05)' : 'rgba(255,255,255,0.02)',
    transition:  'all 0.2s',
    marginBottom:'20px',
  }),
  dropIcon:  { fontSize: '48px', marginBottom: '12px' },
  dropText:  { fontSize: '15px', color: '#e2e8f0', fontWeight: '600', marginBottom: '4px' },
  dropSub:   { fontSize: '12px', color: 'rgba(255,255,255,0.35)' },

  preview:   { position: 'relative', marginBottom: '20px' },
  img:       { width: '100%', borderRadius: '12px', border: '1px solid rgba(0,212,255,0.2)', maxHeight: '500px', objectFit: 'contain', background: '#000' },
  clearBtn:  { position: 'absolute', top: '12px', right: '12px', padding: '6px 12px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.7)', color: '#fff', cursor: 'pointer', fontSize: '12px' },

  qRow:      { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' },
  qBtn:      { padding: '7px 14px', borderRadius: '6px', border: '1px solid rgba(0,212,255,0.15)', background: 'transparent', color: '#9ca3af', cursor: 'pointer', fontSize: '12px' },
  inputRow:  { display: 'flex', gap: '10px', marginBottom: '20px' },
  input:     { flex: 1, padding: '12px 16px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,212,255,0.2)', borderRadius: '8px', color: '#fff', fontSize: '14px', outline: 'none' },
  analyzeBtn:(dis) => ({ padding: '12px 28px', borderRadius: '8px', border: 'none', background: dis ? '#374151' : THEME.colors.blue, color: dis ? '#6b7280' : '#000', cursor: dis ? 'not-allowed' : 'pointer', fontWeight: '800', fontSize: '14px', fontFamily: THEME.fonts.hud, whiteSpace: 'nowrap' }),

  resultCard:{ background: 'rgba(0,212,255,0.04)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: '12px', padding: '20px', marginBottom: '16px' },
  resultTitle:{ fontSize: '12px', fontWeight: '700', letterSpacing: '2px', color: THEME.colors.blue, marginBottom: '14px', fontFamily: THEME.fonts.hud },
  resultText:{ fontSize: '14px', color: '#e2e8f0', lineHeight: '1.8', whiteSpace: 'pre-wrap' },

  saveRow:   { display: 'flex', gap: '10px', marginTop: '16px' },
  saveBtn:   { padding: '9px 20px', borderRadius: '8px', border: `1px solid ${THEME.colors.blue}44`, background: 'rgba(0,212,255,0.08)', color: THEME.colors.blue, cursor: 'pointer', fontSize: '13px', fontWeight: '700' },

  history:   { marginTop: '32px' },
  histTitle: { fontSize: '11px', fontWeight: '700', letterSpacing: '2px', color: 'rgba(255,255,255,0.3)', marginBottom: '12px' },
  histItem:  { background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px', padding: '14px', marginBottom: '8px', cursor: 'pointer' },
  histMeta:  { fontSize: '11px', color: 'rgba(255,255,255,0.3)', marginBottom: '6px' },
  histPreview:{ fontSize: '13px', color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },

  loading:   { textAlign: 'center', padding: '30px', color: THEME.colors.textMuted },
  error:     { color: '#ef4444', padding: '14px', background: '#ef444411', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' },
};

const QUICK_QUESTIONS = [
  'Full ICT/SMC analysis — find all OBs, FVGs, liquidity levels',
  'Where should I enter? Give entry, SL, TP',
  'Is this bullish or bearish? What is the next move?',
  'Identify all order blocks on this chart',
  'What is the market structure on this chart?',
];

const STORAGE_KEY = 'jarvis_vision_history';

function loadHistory() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'); } catch { return []; }
}
function saveToHistory(entry) {
  const h = loadHistory();
  localStorage.setItem(STORAGE_KEY, JSON.stringify([entry, ...h].slice(0, 20)));
}

export default function ChartVision() {
  const [image,      setImage]      = useState(null);  // base64 string
  const [mimeType,   setMimeType]   = useState('image/png');
  const [imgUrl,     setImgUrl]     = useState(null);   // object URL for preview
  const [question,   setQuestion]   = useState('');
  const [result,     setResult]     = useState('');
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [drag,       setDrag]       = useState(false);
  const [history,    setHistory]    = useState(loadHistory);
  const [expandedIdx,setExpandedIdx]= useState(null);
  const fileRef = useRef();

  const loadFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPG, JPEG, WebP)');
      return;
    }
    setMimeType(file.type);
    const url = URL.createObjectURL(file);
    setImgUrl(url);
    setResult('');
    setError('');

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target.result.split(',')[1];
      setImage(base64);
    };
    reader.readAsDataURL(file);
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDrag(false);
    const file = e.dataTransfer.files[0];
    loadFile(file);
  }, [loadFile]);

  const onPaste = useCallback((e) => {
    const item = Array.from(e.clipboardData.items).find(i => i.type.startsWith('image/'));
    if (item) loadFile(item.getAsFile());
  }, [loadFile]);

  const analyze = async () => {
    if (!image) { setError('Upload a chart screenshot first'); return; }
    setLoading(true);
    setResult('');
    setError('');

    const q = question.trim() || 'Full ICT/SMC analysis — find all OBs, FVGs, liquidity levels and give a trade plan';
    const dynamicPrompt = buildDynamicVisionPrompt(q);
    const fullPrompt = `${dynamicPrompt}\n\nSir's specific question: "${q}"`;

    try {
      const res = await fetch('/api/vision', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image, mimeType, question: fullPrompt }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Vision API failed');

      setResult(data.text);

      const entry = {
        id:       Date.now(),
        date:     new Date().toISOString(),
        question: q,
        result:   data.text,
        thumbUrl: imgUrl,
      };
      saveToHistory(entry);
      setHistory(loadHistory());
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const clear = () => {
    setImage(null);
    if (imgUrl) URL.revokeObjectURL(imgUrl);
    setImgUrl(null);
    setResult('');
    setError('');
  };

  const saveToJournal = () => {
    if (!result) return;
    saveKnowledge({
      type:      'chart_analysis',
      source:    'JARVIS Chart Vision',
      title:     `Chart Analysis — ${new Date().toLocaleDateString('en-IN')}`,
      raw_content: result,
      confidence: 80,
    });
    alert('Saved to Knowledge Base ✅');
  };

  return (
    <div style={S.wrap} onPaste={onPaste}>
      <div style={S.title}>CHART VISION AI</div>
      <div style={S.sub}>
        Upload any trading chart screenshot → JARVIS identifies OBs, FVGs, liquidity, structure, and gives a trade plan.
        <br/>Uses Groq llama-4-scout (free vision model). You can also Ctrl+V to paste from clipboard.
      </div>

      {error && <div style={S.error}>{error}</div>}

      {/* Upload zone */}
      {!imgUrl ? (
        <div
          style={S.dropzone(drag)}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current.click()}
        >
          <div style={S.dropIcon}>📊</div>
          <div style={S.dropText}>Drop chart screenshot here or click to upload</div>
          <div style={S.dropSub}>PNG, JPG, WebP · Or press Ctrl+V to paste from clipboard</div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => loadFile(e.target.files[0])} />
        </div>
      ) : (
        <div style={S.preview}>
          <img src={imgUrl} alt="Chart" style={S.img} />
          <button style={S.clearBtn} onClick={clear}>✕ Clear</button>
        </div>
      )}

      {/* Quick questions */}
      <div style={S.qRow}>
        {QUICK_QUESTIONS.map(q => (
          <button key={q} style={S.qBtn} onClick={() => setQuestion(q)}
            onMouseEnter={e => e.currentTarget.style.color = THEME.colors.blue}
            onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}
          >
            {q.length > 45 ? q.slice(0, 42) + '...' : q}
          </button>
        ))}
      </div>

      {/* Question + Analyze */}
      <div style={S.inputRow}>
        <input
          style={S.input}
          placeholder="Ask specific question about the chart... (or leave blank for full analysis)"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && !loading && analyze()}
        />
        <button style={S.analyzeBtn(!image || loading)} onClick={analyze} disabled={!image || loading}>
          {loading ? 'Analyzing...' : 'Analyze Chart'}
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div style={S.loading}>
          🔍 JARVIS is reading your chart... identifying OBs, FVGs, liquidity levels...
        </div>
      )}

      {/* Result */}
      {result && !loading && (
        <div style={S.resultCard}>
          <div style={S.resultTitle}>JARVIS CHART ANALYSIS</div>
          <div style={S.resultText}>{result}</div>
          <div style={S.saveRow}>
            <button style={S.saveBtn} onClick={saveToJournal}>Save to Knowledge Base</button>
            <button style={S.saveBtn} onClick={() => { setResult(''); setQuestion(''); }}>Clear Result</button>
          </div>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div style={S.history}>
          <div style={S.histTitle}>RECENT ANALYSES ({history.length})</div>
          {history.slice(0, 10).map((h, i) => (
            <div key={h.id} style={S.histItem} onClick={() => setExpandedIdx(expandedIdx === i ? null : i)}>
              <div style={S.histMeta}>
                {new Date(h.date).toLocaleString('en-IN')} · "{h.question}"
              </div>
              {expandedIdx === i ? (
                <div style={{ fontSize: '13px', color: '#e2e8f0', lineHeight: '1.7', marginTop: '8px', whiteSpace: 'pre-wrap' }}>
                  {h.result}
                </div>
              ) : (
                <div style={S.histPreview}>{h.result}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
